<template>
  <div class="pre-sales-test-tab">
    <!-- Total Number -->
    <div class="pst-total-row">
      <span class="pst-total-label">Total Number:</span>
      <span class="pst-total-value">{{ totalCount }}</span>
      <span class="pst-total-unit">items</span>
    </div>

    <!-- Query Area -->
    <el-form :model="query" ref="queryForm" label-position="left" label-width="140px" class="pst-query" @keyup.enter.native="handleSearch">
      <el-row>
        <el-col :xl="6" :lg="8" :md="12">
          <el-form-item label="Project Name">
            <el-input clearable size="small" v-model="query.projectName" placeholder="Please input"></el-input>
          </el-form-item>
        </el-col>
        <el-col :xl="6" :lg="8" :md="12">
          <el-form-item label="Product Category">
            <el-select clearable size="small" v-model="query.productCategory" placeholder="Please select" style="width:100%">
              <el-option v-for="c in productCategoryOptions" :key="c.value" :label="c.label" :value="c.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xl="6" :lg="8" :md="12">
          <el-form-item label="Test Status">
            <el-select clearable size="small" v-model="query.testStatus" placeholder="Please select" style="width:100%">
              <el-option v-for="s in testStatusOptions" :key="s.value" :label="s.label" :value="s.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xl="6" :lg="8" :md="12">
          <el-form-item label="POC Labour Hour">
            <div class="pst-range-pair">
              <el-input clearable size="small" v-model="query.pocLabourHourMin" placeholder="Min" style="width:calc(50% - 12px)"></el-input>
              <span class="pst-range-sep">—</span>
              <el-input clearable size="small" v-model="query.pocLabourHourMax" placeholder="Max" style="width:calc(50% - 12px)"></el-input>
            </div>
          </el-form-item>
        </el-col>
        <el-col :xl="6" :lg="8" :md="12">
          <el-form-item label="Winrate">
            <el-input clearable size="small" v-model="query.winrate" placeholder="Please input"></el-input>
          </el-form-item>
        </el-col>
        <el-col :xl="6" :lg="8" :md="12">
          <el-form-item label="Satisfaction">
            <el-select clearable size="small" v-model="query.satisfaction" placeholder="Please select" style="width:100%">
              <el-option v-for="s in satisfactionOptions" :key="s.value" :label="s.label" :value="s.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <!-- Advanced fields -->
        <el-col :xl="6" :lg="8" :md="12">
          <el-form-item label="Test Executor">
            <el-select
              clearable
              filterable
              remote
              size="small"
              v-model="query.testExecutor"
              placeholder="Please input"
              :remote-method="filterTestExecutor"
              style="width:100%"
            >
              <el-option v-for="item in testExecutorSearchList" :key="item.empCode" :label="item.userCode" :value="item.empCode"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <!-- Actions -->
        <el-col :xl="6" :lg="16" :md="24" class="pst-btn-col">
          <el-button type="primary" size="small" @click="handleSearch">Search</el-button>
          <el-button size="small" @click="handleReset">Reset</el-button>
          <el-button size="small" :loading="exportLoading" @click="handleExport">Export</el-button>
        </el-col>
      </el-row>
    </el-form>

    <!-- Table -->
    <el-table
      :data="tableData"
      v-loading="tableLoading"
      size="small"
      border
      style="width:100%"
      :header-cell-style="{background:'#F5F5F9'}"
      @sort-change="handleSortChange"
    >
      <el-table-column prop="projectName" label="Project Name" min-width="180" sortable="custom" align="left" fixed="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="productCategory" label="Product Category" min-width="165" sortable="custom" align="left" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="testStatus" label="Test Status" width="130" sortable="custom" align="center" :show-overflow-tooltip="true">
        <template slot-scope="scope">
          <span v-if="scope.row.testStatus" :class="['pst-tag', 'pst-tag-' + statusTagClass(scope.row.testStatus)]">{{ scope.row.testStatus }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="pocLabourHour" label="POC Labour Hour" width="160" sortable="custom" align="center" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="winrate" label="Winrate" width="110" sortable="custom" align="center" :show-overflow-tooltip="true"></el-table-column>
      <el-table-column prop="marketingSatisfaction" label="Marketing Dept. Satisfaction" min-width="235" sortable="custom" align="center" :show-overflow-tooltip="true">
        <template slot-scope="scope">
          <span v-if="scope.row.marketingSatisfaction" :class="['pst-tag', 'pst-tag-' + satisfactionTagClass(scope.row.marketingSatisfaction)]">{{ scope.row.marketingSatisfaction }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="testExecutor" label="Test Executor" width="140" sortable="custom" align="center" :show-overflow-tooltip="true"></el-table-column>
    </el-table>

    <!-- Pagination -->
    <div class="pst-pagination">
      <span class="pst-pagination-total">Total {{ totalCount }}</span>
      <el-pagination
        small
        layout="prev, pager, next, sizes"
        :total="totalCount"
        :current-page.sync="currentPage"
        :page-sizes="[10, 20, 50]"
        :page-size.sync="pageSize"
        @current-change="onPageChange"
        @size-change="onSizeChange"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PreSalesTestTab',
  props: {
    accountInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      // Query
      query: {
        projectName: '',
        productCategory: '',
        testStatus: '',
        pocLabourHourMin: '',
        pocLabourHourMax: '',
        winrate: '',
        satisfaction: '',
        testExecutor: '',
      },
      productCategoryOptions: [],
      testStatusOptions: [],
      satisfactionOptions: [],
      // Table
      tableData: [],
      tableLoading: false,
      totalCount: 0,
      currentPage: 1,
      pageSize: 10,
      sortField: '',
      sortOrder: '',
      // Export
      exportLoading: false,
      // Test Executor remote search
      testExecutorSearchList: [],
      searchTimeout: null,
    }
  },
  watch: {
    'accountInfo.id'(val) {
      if (val) {
        this.loadData()
      }
    }
  },
  created() {
    this.initDropdownData()
    if (this.accountInfo.id) {
      this.loadData()
    }
  },
  methods: {
    initDropdownData() {
      let kvUrl = `${this.$store.state.islhurl}keyValue/queryLabelList?`
      const kvKeys = ['TestProductCategory', 'TestStatus', 'Satisfaction']
      kvKeys.forEach((k, i) => { kvUrl += (i === 0 ? '' : '&') + `keyList=${k}` })
      this._get(kvUrl).then(res => {
        if (res.code === 200 && res.data) {
          this.productCategoryOptions = res.data.TestProductCategory || []
          this.testStatusOptions = res.data.TestStatus || []
          this.satisfactionOptions = res.data.Satisfaction || []
        }
      }).catch(() => {})
    },
    loadData() {
      const id = this.accountInfo.id
      if (!id) return
      this.tableLoading = true
      const params = this.buildQueryParams()
      params.pageNum = this.currentPage
      params.pageSize = this.pageSize
      let url = `${this.$store.state.islhurl}valued-account/business/${id}/poc`
      const qs = this.buildQueryString(params)
      if (qs) url += '?' + qs
      this._get(url).then(res => {
        this.tableLoading = false
        if (res.code === 200) {
          this.tableData = res.data || []
          this.totalCount = res.totalSize || 0
        }
      }).catch(() => {
        this.tableLoading = false
      })
    },
    buildQueryParams() {
      const params = {}
      if (this.query.projectName) params.projectName = this.query.projectName
      if (this.query.productCategory) params.productCategory = this.query.productCategory
      if (this.query.testStatus) params.testStatus = this.query.testStatus
      if (this.query.pocLabourHourMin) params.pocLabourHourMin = this.query.pocLabourHourMin
      if (this.query.pocLabourHourMax) params.pocLabourHourMax = this.query.pocLabourHourMax
      if (this.query.winrate) params.winrate = this.query.winrate
      if (this.query.satisfaction) params.marketingSatisfaction = this.query.satisfaction
      if (this.query.testExecutor) params.testExecutorCode = this.query.testExecutor
      if (this.sortField) {
        const fieldMap = {
          projectName: 'projectName',
          productCategory: 'productCategory',
          testStatus: 'testStatus',
          pocLabourHour: 'pocLabourHour',
          winrate: 'winrate',
          marketingSatisfaction: 'marketingSatisfaction',
          testExecutor: 'testExecutor',
        }
        params.sortField = fieldMap[this.sortField] || this.sortField
        params.sortOrder = this.sortOrder === 'ascending' ? 'asc' : 'desc'
      }
      return params
    },
    buildQueryString(params) {
      return Object.keys(params)
        .filter(k => params[k] !== undefined && params[k] !== null && params[k] !== '')
        .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
        .join('&')
    },
    handleSearch() {
      this.currentPage = 1
      this.loadData()
    },
    handleReset() {
      this.query = {
        projectName: '',
        productCategory: '',
        testStatus: '',
        pocLabourHourMin: '',
        pocLabourHourMax: '',
        winrate: '',
        satisfaction: '',
        testExecutor: '',
      }
      this.sortField = ''
      this.sortOrder = ''
      this.currentPage = 1
      this.testExecutorSearchList = []
      this.loadData()
    },
    handleSortChange({ prop, order }) {
      this.sortField = order ? (prop || '') : ''
      this.sortOrder = order || ''
      this.currentPage = 1
      this.loadData()
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadData()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadData()
    },
    statusTagClass(status) {
      return { 'Completed': 'green', 'In-progress': 'blue', 'Pending': 'orange', 'Failed': 'red', 'On Going': 'blue' }[status] || 'gray'
    },
    satisfactionTagClass(satisfaction) {
      return { 'Very Satisfied': 'green', 'Satisfied': 'blue', 'Neutral': 'gray', 'Dissatisfied': 'red' }[satisfaction] || 'gray'
    },
    handleExport() {
      const id = this.accountInfo.id
      if (!id) return
      this.exportLoading = true
      const params = this.buildQueryParams()
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null || params[key] === '') delete params[key]
      })
      this.$axios({
        url: `${this.$store.state.islhurl}valued-account/business/${id}/poc/export`,
        method: 'get',
        params,
        responseType: 'blob'
      }).then(response => {
        this.exportLoading = false
        const blob = new Blob([response.data])
        const aEle = document.createElement('a')
        const href = window.URL.createObjectURL(blob)
        aEle.href = href
        const d = new Date()
        const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
        aEle.download = 'PreSalesTest_' + dateStr + '.xlsx'
        document.body.appendChild(aEle)
        aEle.click()
        document.body.removeChild(aEle)
        window.URL.revokeObjectURL(href)
      }).catch(() => {
        this.exportLoading = false
        this.$message.error('Export failed!')
      })
    },
    // Test Executor remote search
    filterTestExecutor(query) {
      if (query !== '') {
        if (this.searchTimeout) clearTimeout(this.searchTimeout)
        this.searchTimeout = setTimeout(() => {
          this.fetchTestExecutorOptions(query)
        }, 200)
      } else {
        this.testExecutorSearchList = []
      }
    },
    fetchTestExecutorOptions(query) {
      let url = `${this.$store.state.islhurl}user/select-option?keyword=${query}`
      this._get(url).then(res => {
        if (res.code === 200 && res.data) {
          this.testExecutorSearchList = res.data
        } else {
          this.testExecutorSearchList = []
        }
      }).catch(() => {
        this.testExecutorSearchList = []
      })
    },
  },
}
</script>

<style lang="less" scoped>
.pre-sales-test-tab {
  // Total Number
  .pst-total-row {
    padding-bottom: 4px;
    margin-bottom: 8px;
    margin-top: -12px;
    .pst-total-label {
      font-size: 13px;
      color: #6B7280;
      font-weight: 500;
    }
    .pst-total-value {
      font-size: 16px;
      color: #007AFF;
      font-weight: 600;
      margin: 0 4px;
    }
    .pst-total-unit {
      font-size: 13px;
      color: #6B7280;
    }
  }

  // Query
  .pst-query {
    padding: 8px 0 5px 0;
    /deep/ .el-col {
      margin-bottom: 7px;
    }
    /deep/ .el-form-item {
      margin-bottom: 0;
      padding: 0 15px 0 0;
    }
    /deep/ .el-form-item__label {
      font-size: 13px;
      color: #1F2937;
      text-align: right;
    }
    /deep/ .el-select {
      width: 100%;
    }
    /deep/ .el-form-item__content .el-input__suffix {
      left: auto;
      right: 10px;
    }
    /deep/ .el-range-editor.el-input__inner {
      width: 100%;
    }
  }
  .pst-range-pair {
    display: flex;
    align-items: center;
    gap: 0;
    .pst-range-sep {
      margin: 0 6px;
      color: #6B7280;
      font-size: 13px;
    }
  }
  .pst-btn-col {
    padding-top: 0;
    text-align: right;
  }

  // Table Tags
  .pst-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 12px;
    line-height: 20px;
    &.pst-tag-green { background: #f6ffed; color: #52C41A; border: 1px solid #b7eb8f; }
    &.pst-tag-blue { background: #e6f7ff; color: #1890FF; border: 1px solid #91d5ff; }
    &.pst-tag-orange { background: #fff7e6; color: #FA8C16; border: 1px solid #ffd591; }
    &.pst-tag-red { background: #fff1f0; color: #F5222D; border: 1px solid #ffa39e; }
    &.pst-tag-gray { background: #fafafa; color: #8C8C8C; border: 1px solid #d9d9d9; }
  }

  // Pagination
  .pst-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 12px 0 0;
    gap: 12px;
    .pst-pagination-total {
      font-size: 13px;
      color: #6B7280;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/view/valuedAccountManagement/accountDetailTabs/PreSalesTest.vue